import React, { useContext, useMemo } from "react";

import { ChasablesContext } from "../../context/ChasablesContext";
import ActionableCard from "../ActionableCard";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import Grid from "@mui/material/Grid2";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { ActionTypeEnum } from "../../models/ChasableModels";
import orderBy from "lodash-es/orderBy";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Mention } from "./Mention";
import { useSearchParams } from "react-router-dom";
import groupBy from "lodash-es/groupBy";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useUsers } from "../../hooks/useUsers";
import { UserModel } from "../../models/UserModels";

type NotificationMode = "all" | "me" | "other";

const modeButtonSx =
  (active: boolean, disabled = false) =>
  (theme: {
    palette: {
      background: { paper: string };
      text: { primary: string; secondary: string };
    };
    shadows: string[];
  }) => ({
    px: 1,
    py: 0.25,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    lineHeight: 1.5,
    border: 0,
    borderRadius: "4px",
    cursor: disabled ? "default" : "pointer",
    bgcolor: active ? theme.palette.background.paper : "transparent",
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    boxShadow: active ? theme.shadows[1] : "none",
    opacity: disabled ? 0.4 : 1,
  });

const Notifications = () => {
  const [_, setSearchParams] = useSearchParams();
  const { chasables, mentions, assignToMe, assignedToUserId, onAssignedToUserIdChange, assignedToMeDisabled } =
    useContext(ChasablesContext);
  const { currentUser } = useCurrentUser();
  const { users } = useUsers();
  const [isNotificationOpen, setIsNotificationOpen] = React.useState(false);
  const [isSelectingOther, setIsSelectingOther] = React.useState(false);

  const otherUsers = useMemo(
    () => users.filter((user) => user.id !== currentUser?.id).sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [users, currentUser?.id]
  );

  const isAllMode = assignedToUserId === null && !isSelectingOther;
  const isOtherMode = isSelectingOther || (!!assignedToUserId && assignedToUserId !== currentUser?.id);
  const selectedOtherUser = otherUsers.find((user) => user.id === assignedToUserId) ?? null;

  const onOpenNotification = () => {
    setIsNotificationOpen(true);
    setSearchParams({ commentId: "" });
  };

  const toggleNotifications = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const handleModeChange = (mode: NotificationMode) => {
    if (mode === "me" && assignedToMeDisabled) return;

    if (mode === "all") {
      setIsSelectingOther(false);
      onAssignedToUserIdChange(null);
      return;
    }

    if (mode === "me") {
      setIsSelectingOther(false);
      onAssignedToUserIdChange(currentUser?.id ?? null);
      return;
    }

    setIsSelectingOther(true);
  };

  const handleOtherUserChange = (_: unknown, user: UserModel | null) => {
    if (!user) return;
    onAssignedToUserIdChange(user.id);
    setIsSelectingOther(true);
  };

  React.useEffect(() => {
    if (!isNotificationOpen) {
      setIsSelectingOther(!!assignedToUserId && assignedToUserId !== currentUser?.id);
    }
  }, [isNotificationOpen, assignedToUserId, currentUser?.id]);

  const newInvoices = chasables?.actions.filter(({ type }) => type === ActionTypeEnum.NewInvoice) ?? [];
  const chasableInvoices = chasables?.actions.filter(({ type }) => type === ActionTypeEnum.Chasable) ?? [];
  const promiseToPayBroken = chasables?.actions.filter(({ type }) => type === ActionTypeEnum.PromiseToPayBroken) ?? [];
  const sortedMentions = orderBy(mentions, ({ createdAt }) => new Date(createdAt).getTime(), "desc");

  const totalChasablesInvoicesByGroup = React.useMemo(() => {
    const groupedChasables = groupBy(chasableInvoices, (action) => action.groupId || action.id);
    return Object.keys(groupedChasables).length;
  }, [chasableInvoices]);

  const totalNotifications =
    newInvoices.length + totalChasablesInvoicesByGroup + sortedMentions.length + promiseToPayBroken.length;

  return (
    <>
      <IconButton onClick={onOpenNotification}>
        <Badge
          badgeContent={
            totalNotifications ? <Typography sx={{ fontSize: "11px" }}>{totalNotifications}</Typography> : null
          }
          color="primary"
        >
          <NotificationsNoneIcon style={{ width: "25px", height: "25px" }} />
        </Badge>
      </IconButton>

      <Drawer
        open={isNotificationOpen}
        onClose={toggleNotifications}
        anchor="right"
        PaperProps={{ sx: { width: "100%", height: "100%" } }}
      >
        <Stack spacing={1} pl={2} pr={1} py={1}>
          <Grid container={true} alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <NotificationsIcon />
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  p: "2px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.action.hover,
                })}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleModeChange("all")}
                  sx={modeButtonSx(isAllMode)}
                >
                  Everyone
                </Box>
                <Tooltip title={assignedToMeDisabled ? "No receivable assigned to me" : ""}>
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleModeChange("me")}
                      disabled={assignedToMeDisabled}
                      sx={modeButtonSx(assignToMe && !isSelectingOther, assignedToMeDisabled)}
                    >
                      Yours
                    </Box>
                  </Box>
                </Tooltip>
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleModeChange("other")}
                  sx={modeButtonSx(isOtherMode)}
                >
                  Others
                </Box>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setIsNotificationOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Grid>
          {isOtherMode && (
            <Autocomplete
              size="small"
              options={otherUsers}
              value={selectedOtherUser}
              getOptionLabel={(option) => option.fullName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={handleOtherUserChange}
              renderInput={(params) => <TextField {...params} label="User" placeholder="Select a user" />}
              sx={{ maxWidth: 320 }}
            />
          )}
        </Stack>
        <Card>
          <CardContent sx={{ height: "100%", overflowY: "auto" }}>
            {totalNotifications > 0 ? (
              <Stack spacing={1}>
                {!!newInvoices.length && (
                  <ActionableCard
                    title={`${newInvoices.length} New Invoice${newInvoices.length > 1 ? "s" : ""}`}
                    link={`chasables/suggested/${newInvoices[0].id}`}
                    handleClick={toggleNotifications}
                  />
                )}
                {!!promiseToPayBroken.length && (
                  <ActionableCard
                    title={`${promiseToPayBroken.length} Promise to Pay Broken${promiseToPayBroken.length > 1 ? "s" : ""}`}
                    link={`chasables/suggested/${promiseToPayBroken[0].id}`}
                    handleClick={toggleNotifications}
                  />
                )}
                {!!totalChasablesInvoicesByGroup && (
                  <ActionableCard
                    title={`${totalChasablesInvoicesByGroup} Reminder${totalChasablesInvoicesByGroup > 1 ? "s" : ""}`}
                    link={`chasables/suggested/${chasableInvoices[0].id}`}
                    handleClick={toggleNotifications}
                  />
                )}
                {sortedMentions.map((mention) => (
                  <Mention key={mention.id} {...mention} onClick={toggleNotifications} />
                ))}
              </Stack>
            ) : (
              <Grid container={true} direction="column" alignContent="center" justifyContent="center" height="100%">
                <Box textAlign="center">
                  <NotificationsIcon fontSize="large" color="primary" />
                </Box>
                <Typography component="p" fontSize={14} fontWeight={700} textAlign="center">
                  You're all caught up!
                </Typography>
                <Typography component="p" textAlign="center" color="textSecondary">
                  You have no new notifications
                </Typography>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Drawer>
    </>
  );
};

export default Notifications;
